import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { HealthStatus, Server, State } from '../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../button/button.component';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroSelectConfig } from '../../../forms/fields/select/ziro-select.component';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { Observable, Subscriber, Subscription } from 'rxjs';
import { ServersContext } from '../../contexts/servers.context';
import { isEqual } from 'lodash';
import { HtmlCheckboxType, ZiroCheckboxConfig } from '../../../forms/fields/checkbox/ziro-checkbox.component';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ModalBodyClass } from '../../../modals/modal-body';
import { DetailedModalComponent } from '../../../modals/detailed-modal/detailed-modal.component';
import { map } from 'rxjs/operators';

@Component({
  selector: 'ziro-add-edit-server-modal-component',
  templateUrl: './add-edit-server-modal.component.html',
  standalone: false,
})
export class AdminAddEditServerModalComponent extends ModalBodyClass implements OnInit, OnDestroy {
  private serversContext = inject(ServersContext);
  protected _ziroFormStateService: ZiroFormStateService;
  private modalComponent = inject<DetailedModalComponent<any>>(DetailedModalComponent);

  ziroIcons = ZiroIcons;
  buttonSizes = ButtonSizes;
  buttonStyles = ButtonStyles;

  hasConnectionError = false;
  hasCredentialsError = false;
  cachedFormData: Server;
  isSubmitted = false;
  connectionError: HealthStatus;

  validators = {
    isDescriptionUnique: (val: string): ZiroFormsValidationState =>
      this._isUnique('description', val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    isHostAddressUnique: (val: string): ZiroFormsValidationState =>
      this._isUnique('hostAddress', val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
    isConnectionWorking: (): ZiroFormsValidationState =>
      this.hasConnectionError ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID,
    areCredentialsCorrect: (): ZiroFormsValidationState =>
      this.hasCredentialsError ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID,
  };
  formConfig = {
    fields: {
      description: {
        label: 'tkey;admin.servers.description.label',
        placeholder: '',
        dataAutomation: 'server-add-edit-description',
        required: true,
        validation: [
          {
            validator: this.validators.isDescriptionUnique,
            message: 'tkey;admin.servers.error.duplicate.description.text',
          },
        ],
      },
      hostAddress: {
        label: 'tkey;admin.servers.host.address.label',
        placeholder: '',
        dataAutomation: 'server-add-edit-host-address',
        required: true,
        validation: [
          {
            validator: this.validators.isHostAddressUnique,
            message: 'tkey;admin.servers.modal.new.duplicate_host_address',
          },
          {
            validator: this.validators.isConnectionWorking,
            message: '',
          },
        ],
      },
      port: {
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        label: 'tkey;admin.servers.port.label',
        placeholder: '',
        dataAutomation: 'server-add-edit-port',
        required: true,
        validation: [
          {
            validator: this.validators.isConnectionWorking,
            message: '',
          },
        ],
      },
      serverType: {
        label: 'tkey;admin.servers.server.type.label',
        placeholder: '',
        dataAutomation: 'server-add-edit-server-type',
        componentConfig: new ZiroSelectConfig({ options: ['CUCM', 'IM & Presence', 'Unity', 'UCCX', 'PCCE'] }),
        required: true,
        validation: [
          {
            validator: this.validators.isConnectionWorking,
            message: '',
          },
        ],
      },
      username: {
        label: 'tkey;admin.servers.username.label',
        placeholder: '',
        dataAutomation: 'server-add-edit-username',
        required: true,
        validation: [
          {
            validator: this.validators.areCredentialsCorrect,
            message: '',
          },
        ],
      },
      password: {
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
        label: 'tkey;admin.servers.password.label',
        placeholder: '',
        dataAutomation: 'server-add-edit-password',
        required: () =>
          this.modalComponent.modalViewProperties.server == null ||
          this.modalComponent.modalViewProperties.server.id == null,
        validation: [
          {
            validator: this.validators.areCredentialsCorrect,
            message: '',
          },
        ],
      },
      disabled: {
        dataAutomation: 'server-add-edit-disable',
        label: 'tkey;admin.servers.disable.label',
        helpText: 'tkey;admin.servers.disable.helptext',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH }),
      },
    },
  } as ZiroFormConfig;

  private _subs = new Subscription();

  ngOnInit(): void {
    this.setIsExisting(!!this.modalComponent.modalViewProperties.server?.username);
    this._setDefaultValue();

    const validateAndSubmitSourceSub = this._validateAndSubmitSource.subscribe(() => {
      this.isSubmitted = true;
    });
    this._subs.add(validateAndSubmitSourceSub);

    this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<Server>) => {
      this._updateErrorStateIfValueChange(data);
      this._updatePortBasedOnServerType(data);
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
  }

  submit(): Observable<Server> {
    this.isSubmitted = true;
    this.hasConnectionError = false;
    this.hasCredentialsError = false;
    this.connectionError = null;
    this.cachedFormData = { ...this.formData };

    if (!this.entity.disabled) {
      return new Observable((subscriber: Subscriber<Server>) => {
        this._testConnection().subscribe((data) => {
          this.connectionError = data;
          if (this.connectionError.state === State.ERROR) {
            if (this.connectionError.cause === 'Connection Issue') {
              this.hasConnectionError = true;
              this.fieldChannels['hostAddress'].validateSource.next();
              this.fieldChannels['port'].validateSource.next();
              this.fieldChannels['serverType'].validateSource.next();
            } else if (this.connectionError.cause === 'Incorrect Credentials') {
              this.hasCredentialsError = true;
              this.fieldChannels['username'].validateSource.next();
              this.fieldChannels['password'].validateSource.next();
            }

            this._validateAndSubmitSource.next(false);
            subscriber.error();
            subscriber.complete();
          } else {
            this.modalComponent.modalViewProperties.submitFn(this.formData).subscribe(() => {
              subscriber.next(this.formData);
              subscriber.complete();
            });
          }
        });
      });
    } else {
      return this.modalComponent.modalViewProperties.submitFn(this.formData).pipe(map(() => this.formData));
    }
  }

  private _testConnection = (): Observable<HealthStatus> => {
    return this.serversContext.testConnection(this.formData);
  };

  private _setDefaultValue(): void {
    this.entitySource.next({
      ...this.modalComponent.modalViewProperties.server,
    });
  }

  private _isUnique(comparisonKey: keyof Server, currentValue: string): boolean {
    return (
      !this.entity ||
      !this.modalComponent.modalViewProperties.servers.find(
        (server: Server) =>
          String(server[comparisonKey]).toLowerCase() === currentValue.trim().toLowerCase() &&
          server.id !== this.entity.id,
      )
    );
  }

  /**
   * Change the port based on the server type selected
   */
  private _updatePortBasedOnServerType(data: ZiroFormsUpdate<Server>): void {
    if (data.new && data.old && !isEqual(data.new.serverType, data.old?.serverType)) {
      if (data.new.serverType === 'PCCE') {
        this.entitySource.next({
          ...this.formData,
          port: 443,
        });
      } else {
        this.entitySource.next({
          ...this.formData,
          port: 8443,
        });
      }
    }
  }

  /**
   * Clear the manually placed errors if changes have been made to the form
   */
  private _updateErrorStateIfValueChange(data: ZiroFormsUpdate<Server>): void {
    if (!isEqual(data.new, data.old) && this.connectionError) {
      this.hasConnectionError = false;
      this.hasCredentialsError = false;
      Object.values(this.fieldChannels).forEach((field) => field.validateSource.next());
    }
  }
}
